import React from 'react';
import {useTranslation} from 'react-i18next';

/**
 * Quién consulta los datos de este cliente: el usuario del portal o el equipo de la agencia.
 *
 * Existe para que la agencia no lea como adopción lo que es su propio trabajo interno. Sobre los
 * mismos datos consultan las dos partes, y el número grande —"34 consultas"— cambia de significado
 * por completo según de qué lado vino.
 *
 * **"Sin atribuir" nunca se reparte** entre los dos lados: son interacciones cuyo usuario no quedó
 * registrado. Repartirlas por proporción daría un número más lindo y falso. Aparece solo cuando es
 * mayor que cero, y por diseño del backend una fila sin atribuir tampoco alcanza para dar por
 * activo a un cliente: si el encabezado del ranking dice "4 de 71", tiene que ser verdad.
 */
export function PortalUsageActorSplit({assistant}) {
    const {t} = useTranslation();

    // Un solo helper para los TRES lados. Si uno leyera `queries` y otro `interactions` durante el
    // skew, la barra mentiría el reparto — que es justo lo que esta caja existe para mostrar.
    const clientCount = actorCount(assistant, 'client');
    const agencyCount = actorCount(assistant, 'agency');
    const unattributed = actorCount(assistant, 'unattributed');

    const attributed = clientCount + agencyCount;
    if (!attributed && !unattributed) return null;

    // La barra reparte SOLO lo atribuido: meter el resto en una tercera franja invitaría a leerlo
    // como un actor más, que es justo lo que no es.
    const clientPct = attributed ? Math.round((clientCount / attributed) * 100) : 0;

    return (
        <div className="pu-split">
            <div className="pu-split__bar" aria-hidden="true">
                <i className="-client" style={{width: `${clientPct}%`}}/>
                <i className="-agency" style={{width: `${100 - clientPct}%`}}/>
            </div>
            <div className="pu-split__legend">
                <span className="pu-split__item">
                    <span className="pu-split__dot -client"/>
                    {t('portal_usage.split_client', 'Usuario del portal')} <b>{clientCount}</b>
                </span>
                <span className="pu-split__item">
                    <span className="pu-split__dot -agency"/>
                    {t('portal_usage.split_agency', 'Equipo de la agencia')} <b>{agencyCount}</b>
                </span>
                {unattributed > 0 ? (
                    <span className="pu-split__item -muted">
                        {t('portal_usage.split_unattributed', 'Sin atribuir')} <b>{unattributed}</b>
                    </span>
                ) : null}
            </div>
        </div>
    );
}

/** Las consultas de un actor, cayendo al contador viejo si el backend todavía no manda el split. */
function actorCount(assistant, actor) {
    const counters = (assistant && assistant[actor]) || {};
    return typeof counters.queries === 'number' ? counters.queries : (counters.interactions || 0);
}

export default PortalUsageActorSplit;
